// Role detail panel — 80% right panel with header and tabbed content.
// Phase 3: tabs rendered, only Members tab placeholder active.
// Phases 4-6 will implement tab content.

import { useState } from 'react'
import type { Role } from '@/lib/api/usersRoles'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { MembersTab } from '@/components/features/roles/MembersTab'
import {
  RegisterPermissionTab,
  ControlPermissionTab,
  ActionPermissionTab,
  EventPermissionTab,
  FrameworkPermissionTab,
} from '@/components/features/roles/DomainPermissionTabs'
import { Pencil, Trash2 } from 'lucide-react'

const tabDefs = [
  { id: 'members', label: 'Members' },
  { id: 'registers', label: 'Registers' },
  { id: 'controls', label: 'Controls' },
  { id: 'actions', label: 'Actions' },
  { id: 'events', label: 'Events' },
  { id: 'frameworks', label: 'Frameworks' },
] as const

interface RoleDetailProps {
  role: Role
  onEditClick: () => void
  onDeleteClick: () => void
}

export function RoleDetail({ role, onEditClick, onDeleteClick }: RoleDetailProps) {
  const [activeTab, setActiveTab] = useState<string>('members')

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-start justify-between border-b px-6 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-xl font-semibold">{role.name}</h2>
          {role.description && (
            <p className="mt-1 text-sm text-muted-foreground">{role.description}</p>
          )}
        </div>
        <div className="ml-4 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onEditClick}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={onDeleteClick}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col">
        <div className="border-b px-6">
          <TabsList className="h-auto bg-transparent p-0">
            {tabDefs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="rounded-none border-b-2 border-transparent px-4 py-2.5 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="members" className="m-0 p-6">
            <MembersTab roleId={role.id} roleName={role.name} />
          </TabsContent>
          <TabsContent value="registers" className="m-0 p-4">
            <RegisterPermissionTab roleId={role.id} />
          </TabsContent>
          <TabsContent value="controls" className="m-0 p-4">
            <ControlPermissionTab roleId={role.id} />
          </TabsContent>
          <TabsContent value="actions" className="m-0 p-4">
            <ActionPermissionTab roleId={role.id} />
          </TabsContent>
          <TabsContent value="events" className="m-0 p-4">
            <EventPermissionTab roleId={role.id} />
          </TabsContent>
          <TabsContent value="frameworks" className="m-0 p-4">
            <FrameworkPermissionTab roleId={role.id} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}
